import React from "react";
import { AlertTriangle } from "lucide-react";
import { LineupPill, StatusPill, na, pct, time } from "@/components/fbShared";

const TipSide = ({ l, prob, abbr }) => (
  <div className="min-w-0 flex-1 rounded-lg border border-slate-800 bg-slate-950/60 p-2" data-testid={`fb-tip-${abbr}`}>
    <p className="truncate font-display text-[12px] font-bold text-orange-200">{l.jumper || "Jumper unknown"}</p>
    <p className="font-mono text-[9px] text-slate-500">{l.jumper_reason || "—"}</p>
    {l.tip ? (
      <p className="mt-1 font-mono text-[10px] text-slate-300">
        {l.tip.wins}-{l.tip.losses} verified · {na(l.tip.pct, "%")} · last {l.tip.recent_attempts}: {l.tip.recent_wins}-{l.tip.recent_attempts - l.tip.recent_wins}
      </p>
    ) : <p className="mt-1 font-mono text-[10px] text-slate-500">Tip record N/A</p>}
    <p className="font-mono text-[11px] font-bold text-orange-300">{prob == null ? "N/A" : `${Math.round(prob * 100)}% to win tip`}</p>
  </div>
);

export const TipPanel = ({ g }) => (
  <div className="space-y-1.5" data-testid={`fb-tip-panel-${g.game._id}`}>
    <div className="flex gap-2">
      <TipSide l={g.lineups.away} prob={g.tip.away_prob} abbr={g.game.away.abbr} />
      <TipSide l={g.lineups.home} prob={g.tip.home_prob} abbr={g.game.home.abbr} />
    </div>
    <p className="font-mono text-[9px] text-slate-500">
      Expected opening possession: <b className="text-slate-300">{g.tip.expected_possession || "N/A"}</b> · {g.tip.known ? "both jumpers ≥5 verified tips" : "low tip sample — qualified estimate"} · {g.tip.calibrated ? "tip model calibrated on backtest" : "tip estimate not calibrated"} · team with the ball scores first {pct(g.tip.possession_scores_first, 0)} of the time
    </p>
  </div>
);

const Lineup = ({ l, abbr }) => (
  <div className="min-w-0 flex-1" data-testid={`fb-lineup-${abbr}`}>
    <div className="mb-1 flex items-center gap-1.5"><span className="font-display text-[12px] font-bold text-slate-200">{abbr}</span><LineupPill s={l.status} /></div>
    {l.players.length ? l.players.map((p) => (
      <p key={p.id} className="truncate font-mono text-[10px] text-slate-300"><span className="inline-block w-6 text-slate-500">{p.pos || "?"}</span>{p.name}</p>
    )) : <p className="font-mono text-[10px] text-rose-300">Lineup unavailable</p>}
    <p className="mt-0.5 truncate font-mono text-[8px] text-slate-600">{l.source || ""}</p>
  </div>
);

const TeamTop = ({ rows, abbr, onPlayer, gid }) => (
  <div className="min-w-0 flex-1 rounded-lg border border-orange-900/50 bg-orange-950/10 p-2" data-testid={`fb-top2-${gid}-${abbr}`}>
    <p className="mb-1 font-mono text-[9px] uppercase tracking-wider text-orange-400">{abbr} · top 2 first-basket</p>
    {rows.length ? rows.map((c, i) => (
      <button key={c.id} type="button" onClick={() => onPlayer(c.id)} data-testid={`fb-top2-${gid}-${abbr}-${i}`}
        className="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left font-mono text-[10px] transition-colors hover:bg-slate-900">
        <span className="text-slate-600">{i + 1}</span>
        <span className="min-w-0 flex-1 truncate font-bold text-orange-100">{c.name} <span className="font-normal text-slate-500">{c.pos || ""}{c.jumper ? " · jumper" : ""}</span></span>
        <span className="text-orange-300">{c.prob_calibrated ? pct(c.prob) : "—"}</span>
        <span className="text-slate-400">{na(c.fusion)}</span>
        <StatusPill s={c.status} />
      </button>
    )) : <p className="font-mono text-[10px] text-slate-600">No lineup yet.</p>}
  </div>
);

export const GameCard = ({ g, onPlayer }) => {
  const s = g.game;
  const warn = [g.lineups.away.personnel_warning, g.lineups.home.personnel_warning, ...g.warnings].filter(Boolean);
  return (
    <div className="space-y-2.5 rounded-xl border border-slate-800 bg-[#0B0F17] p-3" data-testid={`fb-game-${s._id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-display text-sm font-bold uppercase text-slate-100">{s.away.abbr} @ {s.home.abbr}</span>
        <span className="font-mono text-[10px] text-slate-500">{s.state === "pre" ? time(s.start) : s.status}</span>
        {s.season_type === 1 && <span className="rounded bg-slate-800 px-1.5 font-mono text-[9px] text-slate-400">PRESEASON</span>}
        <span className="ml-auto font-mono text-[9px] text-slate-500">data {g.data_status} · {Math.round(g.completeness * 100)}% complete</span>
      </div>
      <div className="flex gap-3"><Lineup l={g.lineups.away} abbr={s.away.abbr} /><Lineup l={g.lineups.home} abbr={s.home.abbr} /></div>
      <TipPanel g={g} />
      {g.top_by_team && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <TeamTop rows={g.top_by_team.away} abbr={s.away.abbr} onPlayer={onPlayer} gid={s._id} />
          <TeamTop rows={g.top_by_team.home} abbr={s.home.abbr} onPlayer={onPlayer} gid={s._id} />
        </div>
      )}
      <div className="grid gap-1.5 sm:grid-cols-2">
        {g.best_candidate && (
          <button type="button" onClick={() => onPlayer(g.best_candidate.id)} data-testid={`fb-best-${s._id}`} className="rounded-lg border border-orange-900/60 bg-orange-950/20 p-2 text-left transition-colors hover:border-orange-500/70">
            <p className="font-mono text-[9px] uppercase tracking-wider text-orange-400">Best first-basket candidate</p>
            <p className="truncate text-[12px] font-bold text-orange-100">{g.best_candidate.name} · {g.best_candidate.team} {g.best_candidate.pos}</p>
            <p className="flex items-center gap-1.5 font-mono text-[10px] text-slate-400">Fusion {na(g.best_candidate.fusion)} · est {pct(g.best_candidate.prob)} <StatusPill s={g.best_candidate.status} /></p>
          </button>
        )}
        {g.best_matchup && (
          <button type="button" onClick={() => onPlayer(g.best_matchup.id)} data-testid={`fb-bestmatch-${s._id}`} className="rounded-lg border border-slate-800 bg-slate-950/60 p-2 text-left transition-colors hover:border-orange-500/60">
            <p className="font-mono text-[9px] uppercase tracking-wider text-slate-500">Best opponent-weakness matchup</p>
            <p className="truncate text-[12px] font-bold text-slate-100">{g.best_matchup.name} · {g.best_matchup.team} {g.best_matchup.pos}</p>
            <p className="font-mono text-[10px] text-slate-400">Matchup {na(g.best_matchup.matchup_score)} · weakness {na(g.best_matchup.weak_index, "x")}</p>
          </button>
        )}
      </div>
      {g.actual && !g.frozen?.result && (
        <p className="rounded-md bg-slate-900 px-2 py-1 font-mono text-[10px] text-slate-300" data-testid={`fb-actual-${s._id}`}>
          First basket: <b className="text-orange-200">{g.actual.name}</b> ({g.actual.method}) · {g.actual.text}
        </p>
      )}
      {g.frozen?.result && (
        <p className="rounded-md bg-slate-900 px-2 py-1 font-mono text-[10px] text-slate-300" data-testid={`fb-result-${s._id}`}>
          First basket: <b className="text-orange-200">{g.frozen.result.first_scorer}</b> ({g.frozen.result.method}) · pre-game rank {na(g.frozen.result.rank)}
        </p>
      )}
      {warn.map((w) => <p key={w} className="flex items-center gap-1 font-mono text-[9px] text-amber-300"><AlertTriangle className="h-3 w-3" />{w}</p>)}
    </div>
  );
};
